import type { ReactNode } from 'react';

export interface SubpageHeaderProps {
  title: string;
  subtitle?: string;
  onBack: () => void;
  /** Optional action on the right (Save / Add / etc.) */
  right?: ReactNode;
  /** Extra sticky content below the title row (tabs, filters, etc.) */
  children?: ReactNode;
  className?: string;
  /** Aria label for the back button */
  backLabel?: string;
}

/**
 * Unified secondary-page sticky header — matches `/me/profile`.
 * Back button + title + optional subtitle; top padding/size are fixed for visual consistency.
 */
export function SubpageHeader({
  title,
  subtitle,
  onBack,
  right,
  children,
  className = '',
  backLabel = 'Go back',
}: SubpageHeaderProps) {
  return (
    <div
      className={`sticky top-0 z-30 bg-white border-b border-gray-100 ${className}`.trim()}
    >
      <div className="px-4 pt-6 pb-2.5 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          aria-label={backLabel}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 cursor-pointer hover:bg-gray-200 transition-colors flex-shrink-0"
        >
          <i className="ri-arrow-left-s-line text-gray-700 text-xl" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-gray-900 font-bold text-base leading-tight truncate">{title}</h1>
          {subtitle ? (
            <p className="text-gray-400 text-[11px] truncate">{subtitle}</p>
          ) : null}
        </div>
        {right ? <div className="flex-shrink-0">{right}</div> : null}
      </div>
      {children}
    </div>
  );
}
